import { PdfData } from "@/models/pdfData";
import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListSubheader,
  styled,
  ToggleButton,
  toggleButtonClasses,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import GameSelectionLine from "@components/controls/GameSelectionLine";
import { GameSelectionDropdownOption } from "@components/controls/GameSelectionDropdown";
import { millisToDate } from "@/services/dateTimeService";

export type LeagueTree = {
  [key in "FEMALE" | "MALE"]: {
    [key in string]: GameSelectionDropdownOption[];
  };
};

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  gap: "1rem",
  [`& .${toggleButtonGroupClasses.firstButton}, & .${toggleButtonGroupClasses.middleButton}`]:
    {
      borderBottomRightRadius: (theme.vars || theme).shape.borderRadius,
      borderBottomLeftRadius: (theme.vars || theme).shape.borderRadius,
    },
  [`& .${toggleButtonGroupClasses.lastButton}, & .${toggleButtonGroupClasses.middleButton}`]:
    {
      borderTopRightRadius: (theme.vars || theme).shape.borderRadius,
      borderTopLeftRadius: (theme.vars || theme).shape.borderRadius,
      borderTop: `1px solid ${(theme.vars || theme).palette.divider}`,
    },
  [`& .${toggleButtonGroupClasses.lastButton}.${toggleButtonClasses.disabled}, & .${toggleButtonGroupClasses.middleButton}.${toggleButtonClasses.disabled}`]:
    {
      borderTop: `1px solid ${(theme.vars || theme).palette.action.disabledBackground}`,
    },
}));

const GameSelectionTreeView: FC<{
  games?: GameSelectionDropdownOption[];
  loadingGames: boolean;
  onGameSelected: (samsId: string) => void;
}> = ({ games, loadingGames, onGameSelected }) => {
  const leagueTree = games ? getLeagueTree(games) : undefined;

  const [selectionModeState, setSelectionModeState] = useState<
    "league" | string
  >("league");
  const [selectedLeagueGames, setSelectedLeagueGames] = useState<
    GameSelectionDropdownOption[]
  >([]);

  useEffect(() => {
    if (leagueTree && selectionModeState !== "league") {
      setSelectedLeagueGames(
        Object.values(leagueTree)
          .flatMap((leagues) => Object.values(leagues))
          .find(
            (games) =>
              games[0]?.gameData?.competition?.shortName === selectionModeState,
          ) || [],
      );
    }
  }, [selectionModeState]);

  return (
    <>
      <Grid
        size={12}
        display={"grid"}
        gridTemplateColumns={"1fr auto 1fr"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        {selectionModeState !== "league" && (
          <Button
            onClick={() => setSelectionModeState("league")}
            sx={{ justifySelf: "start" }}
          >
            {"< Zurück"}
          </Button>
        )}
        <Typography variant={"body1"} textAlign={"center"} gridColumn={2}>
          {selectedLeagueGames?.[0]?.gameData?.competition?.shortName ||
            "Liga auswählen"}
        </Typography>
      </Grid>
      {leagueTree &&
        (selectionModeState === "league" ? (
          Object.entries(leagueTree).map(([gender, leagues]) => {
            return (
              <Grid
                size={6}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  flexDirection: "column",
                  px: 1,
                }}
              >
                <Typography variant={"subtitle1"}>
                  {gender === "MALE" ? "Männer" : "Frauen"}
                </Typography>
                <StyledToggleButtonGroup orientation={"vertical"}>
                  {Object.entries(leagues)
                    .sort(([league1], [league2]) => {
                      if (league1.startsWith("2") && league2.startsWith("2")) {
                        return league1 === "2. BLFP"
                          ? -1
                          : league2 === "2. BLFP"
                            ? 1
                            : league1.localeCompare(league2);
                      } else {
                        return league1.localeCompare(league2);
                      }
                    })
                    .map(([league, games]) => (
                      <ToggleButton
                        value={league}
                        onClick={() => setSelectionModeState(league)}
                      >
                        {games[0]?.gameData?.competition?.name.replace(
                          /Männer|Frauen/i,
                          "",
                        ) || league}
                      </ToggleButton>
                    ))}
                </StyledToggleButtonGroup>
              </Grid>
            );
          })
        ) : (
          <Box sx={{ overflowY: "auto", maxHeight: "100%", width: "100%" }}>
            <List>
              {selectedLeagueGames
                .reduce(
                  (collector, game) => {
                    const dateString = millisToDate(
                      game.gameData.gameStartDateTime,
                    );
                    const existingDateGroup = collector.find(
                      (c) => c.dateString === dateString,
                    );
                    if (existingDateGroup) {
                      existingDateGroup.games.push(game);
                    } else {
                      collector.push({ dateString, games: [game] });
                    }
                    return collector;
                  },
                  [] as {
                    dateString: string;
                    games: GameSelectionDropdownOption[];
                  }[],
                )
                .map(({ dateString, games }) => (
                  <li key={dateString}>
                    <ListSubheader>{dateString}</ListSubheader>
                    {games.map((game) => (
                      <Box
                        onClick={() => onGameSelected(game.samsId)}
                        key={game.samsId}
                        sx={{
                          display: "contents",
                          cursor: "pointer",
                        }}
                      >
                        <GameSelectionLine
                          option={game}
                          showImagesOnDesktop={true}
                        />
                      </Box>
                    ))}
                  </li>
                ))}
            </List>
          </Box>
        ))}
    </>
  );
};

const getLeagueTree = (gameData: GameSelectionDropdownOption[]) =>
  gameData.reduce(
    (leagueTree, game) => {
      const compoetition = game.gameData.competition;
      const gender = compoetition?.gender;
      const league = compoetition?.shortName;
      if (gender && ["MALE", "FEMALE"].includes(gender) && league) {
        const typedGender = gender as "MALE" | "FEMALE";
        if (!leagueTree[typedGender][league]?.length) {
          leagueTree[typedGender][league] = [game];
        } else {
          leagueTree[typedGender][league].push(game);
        }
      }
      return leagueTree;
    },
    { FEMALE: {}, MALE: {} } as LeagueTree,
  );

export default GameSelectionTreeView;
